import { format } from 'date-fns';
import { TattooChoices } from '@prisma/client';
import { capitalize } from '@mui/material';
import {
  Appointment,
  EmployeeToBusiness,
  GeneralRequest,
  LoadedUser,
  ModalData,
  Section,
} from '../../interfaces';
import {
  formatDollarAmount,
  getBooleanText,
  getCleanedPhone,
  getCustomerNameFromAppointment,
  getEmployeeNameFromAppointmentOrGenRequest,
  isNil,
} from '../global/helper';
import { createReferralSourceSection } from '../../helpers/appointments';

// Set modal data based on the appointment data
export function createModalData({
  appointment,
  sizeOptions,
  navigateToCustomer,
  allBusinesses,
  employeesToBusiness,
}: {
  appointment: Appointment | GeneralRequest;
  sizeOptions: TattooChoices[] | undefined;
  navigateToCustomer: (customerId: string) => void;
  allBusinesses: LoadedUser['allBusinesses'];
  employeesToBusiness?: EmployeeToBusiness[];
}) {
  const customerName = getCustomerNameFromAppointment(
    appointment as Appointment,
  );

  const employeeName = getEmployeeNameFromAppointmentOrGenRequest(
    appointment,
    employeesToBusiness,
  );

  // Reformat users phone number
  const finalPhoneNumber = getCleanedPhone(appointment.customer.phoneNumber);

  // Reformat users birthday
  let customerDob = '';
  if (appointment.customer.dob) {
    customerDob = appointment.customer.dob?.split('T')[0];
  }

  // Get boolean variable texts
  const firstTattoo = getBooleanText(appointment.isFirstTattoo);
  const newCustomer = getBooleanText(appointment.isNewCustomer);

  let size = appointment.tattooSize;
  const sizeName = sizeOptions?.find(option => option.value === size);
  if (sizeName) {
    size = sizeName.name;
  }

  const status = (appointment as Appointment).status || '';
  const previousStatus = (appointment as Appointment).previousStatus || '';

  const appointmentBusiness = appointment?.businessId
    ? allBusinesses?.find(b => b.businessId === appointment.businessId)
    : null;

  const newModalData = {
    name: 'Booking Request',
    sections: [
      {
        name: 'Overview',
        rows: [
          {
            type: 'TEXT',
            label: 'Status',
            value: [
              (appointment as Appointment).appointmentId
                ? `${capitalize(status.toLowerCase())}${
                    status === 'ARCHIVED' && previousStatus
                      ? ` (${capitalize(previousStatus.toLowerCase())})`
                      : ''
                  }`
                : 'Requested',
            ],
          },
          {
            type: 'TEXT',
            label: 'Requested On:',
            value: [format(new Date(appointment.createdAt!), 'MMMM d, y')],
          },
          (appointment as Appointment).appointmentId &&
          (appointment as Appointment).status === 'ACCEPTED'
            ? {
                type: 'TEXT',
                label: 'Accepted On',
                value: [
                  format(
                    new Date((appointment as Appointment).acceptedOn!),
                    'MMMM d, y',
                  ),
                ],
              }
            : null,
          {
            type: 'TEXT',
            label: 'Tattoo Artist',
            value: [employeeName || ''],
          },
          (appointment as Appointment).appointmentId &&
          (appointment as Appointment).status === 'REJECTED' &&
          appointment.rejectionMessage
            ? {
                type: 'TEXT',
                label: 'Rejection Message',
                value: [appointment.rejectionMessage],
              }
            : null,
        ].filter(Boolean),
      },
      {
        name: 'Client',
        rows: [
          {
            type: 'TEXT',
            label: 'Name',
            value: [customerName || ''],
          },
          {
            type: 'TEXT',
            label: 'Booking Location',
            value: [
              (appointment as Appointment).tripCity
                ? (appointment as Appointment).tripCity || ''
                : appointmentBusiness?.name || 'Home Studio',
            ],
          },
          {
            type: 'TEXT',
            label: 'Email',
            value: [appointment.customer.email || ''],
          },
          {
            type: 'PHONE',
            label: 'Phone',
            value: [finalPhoneNumber || ''],
          },
          {
            type: 'TEXT',
            label: 'Birthday',
            value: [
              customerDob
                ? format(new Date(`${customerDob}T00:00:00`), 'MM/dd/yyyy')
                : '',
            ],
          },
          {
            type: 'TEXT',
            label: 'IG Handle',
            value: [appointment.customer.igHandle || ''],
          },
          {
            type: 'TEXT',
            label: 'Skin Tone',
            value: [appointment.skinTone || ''],
          },
          {
            type: 'TEXT',
            label: 'First Tattoo',
            value: [firstTattoo || ''],
          },
          {
            type: 'TEXT',
            label: 'New Customer',
            value: [newCustomer || ''],
          },
          {
            type: 'TEXT',
            label: 'Internal Notes',
            value: [appointment.customer.internalNotes || ''],
          },
        ],
        buttonText: 'View',
        buttonAction: () => navigateToCustomer(appointment.customerId),
      },
      {
        name: 'Tattoo Details',
        rows: [
          !isNil(appointment.tattooServiceType) && {
            type: 'TEXT',
            label: 'Tattoo Service Type',
            value: [appointment.tattooServiceType || ''],
          },
          {
            type: 'TEXT',
            label: 'Budget',
            value: [
              appointment.budget ? formatDollarAmount(appointment.budget) : '',
            ],
          },
          {
            type: 'TEXT',
            label: 'Tattoo Location',
            value: [appointment.tattooLocation || ''],
          },
          {
            type: 'TEXT',
            label: 'Tattoo Size',
            value: [size || ''],
          },
          {
            type: 'TEXT',
            label: 'Tattoo Color',
            value: [appointment.tattooColor || ''],
          },
          {
            type: 'TEXT',
            label: 'Tattoo Description',
            value: [appointment.tattooDetails || ''],
          },
          !isNil(appointment.otherNotes) && {
            type: 'TEXT',
            label: 'Other Notes',
            value: [appointment.otherNotes || ''],
          },
          !isNil(appointment.ongoingProject) && {
            type: 'TEXT',
            label: 'Ongoing Project',
            value: [getBooleanText(appointment.ongoingProject) || ''],
          },
          !isNil(appointment.coverUp) && {
            type: 'TEXT',
            label: 'Cover Up',
            value: [getBooleanText(appointment.coverUp) || ''],
          },
          !isNil(appointment.surroundingTattoos) && {
            type: 'TEXT',
            label: 'Surrounding Tattoos',
            value: [getBooleanText(appointment.surroundingTattoos) || ''],
          },
          !isNil(appointment.isEphemeral) && {
            type: 'TEXT',
            label: 'Is Ephemeral?',
            value: [getBooleanText(appointment.isEphemeral) || ''],
          },
          {
            type: 'TEXT',
            label: 'General Availability',
            value: [appointment.availability || ''],
          },
        ].filter(Boolean),
      },
    ] as Section[],
  };

  if ((appointment as Appointment).status === 'ACCEPTED') {
    let priceText = `$${JSON.stringify((appointment as Appointment).price)}`;
    if (
      (appointment as Appointment).priceType &&
      (appointment as Appointment).priceType === 'RANGE'
    ) {
      priceText = `$${JSON.stringify(
        (appointment as Appointment).minPrice,
      )} - $${JSON.stringify((appointment as Appointment).maxPrice)}`;
    }
    if ((appointment as Appointment).priceType === 'HOURLY') {
      priceText = `$${JSON.stringify(
        (appointment as Appointment).hourlyPrice,
      )} per hour`;
    }

    let lengthText = '';
    if (
      appointment &&
      'appointmentLengthInMinutes' in appointment &&
      typeof appointment.appointmentLengthInMinutes === 'number'
    ) {
      const hours = Math.floor(appointment.appointmentLengthInMinutes / 60);
      const minutes = appointment.appointmentLengthInMinutes % 60;

      if (minutes === 0) {
        lengthText = `${hours} hour appointment`;
      } else if (hours === 0) {
        lengthText = `${minutes} minute appointment`;
      } else {
        lengthText = `${hours} hour and ${minutes} minute appointment`;
      }
    }

    newModalData.sections.push({
      name: 'Appointment Details',
      rows: [
        {
          type: 'TEXT',
          label: 'Price Type',
          value: [
            (appointment as Appointment).appointmentId
              ? (appointment as Appointment).priceType || ''
              : '',
          ],
        },
        {
          type: 'TEXT',
          label: 'Price',
          value: [priceText],
        },
        {
          type: 'TEXT',
          label: 'Appointment Length',
          value: [lengthText || ''],
        },
        {
          type: 'TEXT',
          label: 'Deposit',
          value: [
            (appointment as Appointment).appointmentId &&
            (appointment as Appointment).depositPrice
              ? formatDollarAmount((appointment as Appointment).depositPrice)
              : '',
          ],
        },
      ],
    });
  }

  newModalData.sections.push({
    name: 'Photos',
    rows: [
      {
        type: 'IMAGE',
        label: 'Reference Images',
        value: appointment.refImgsId || [''],
      },
      {
        type: 'IMAGE',
        label: 'Area Images',
        value: appointment.areaImgsId || [''],
      },
    ],
  });

  const referralSource = appointment.referralSources?.[0];
  if (referralSource) {
    const referralSourceSection = createReferralSourceSection(referralSource);
    if (referralSourceSection) {
      newModalData.sections.push(referralSourceSection);
    }
  }
  return newModalData as ModalData;
}
